// Sidebar grouping for the chat channel list. Pure helpers, mirrored in
// mobile_app/components/shared/chat/chatSidebarSectionUtils.js (keep in sync).

export const SECTION_PINNED = "pinned";
export const SECTION_DIRECT = "direct";
export const SECTION_CHANNELS = "channels";
export const SECTION_TICKETS = "tickets";
export const SECTION_STORE = "store";
export const SECTION_SUPPORT = "support";

export const SECTION_ORDER = [
  SECTION_PINNED,
  SECTION_DIRECT,
  SECTION_CHANNELS,
  SECTION_TICKETS,
  SECTION_STORE,
  SECTION_SUPPORT,
];

export const SECTION_LABELS = {
  [SECTION_PINNED]: "Pinned",
  [SECTION_DIRECT]: "Direct messages",
  [SECTION_CHANNELS]: "Channels",
  [SECTION_TICKETS]: "Tickets",
  [SECTION_STORE]: "Store",
  [SECTION_SUPPORT]: "Rightwork support",
};

const STORE_CHANNEL_RE = /^store-\d+$/;
const DEPARTMENT_CHANNEL_RE = /^store-\d+-department-/;
const TICKET_CHANNEL_RE = /^store-\d+-(maintenance|it|marketing|menu)$/;
const SUPPORT_CHANNEL_RE = /^company-\d+-support$/;
const DAILY_CHANNEL_RE = /^daily-store-(\d+)-(\d{4}-\d{2}-\d{2})$/;
const INTERNAL_CHANNEL_ID = "rightwork-internal";

export const isTicketChannel = (channel) =>
  channel?.type === "team" && TICKET_CHANNEL_RE.test(channel?.id || "");

// Channels where Rightwork staff talk with a company (and Rightwork's own
// internal channel); they get their own section and avatar treatment
export const isSupportChannel = (channel) =>
  channel?.type === "team" &&
  (SUPPORT_CHANNEL_RE.test(channel?.id || "") ||
    /^support-request-[a-f0-9]{32}$/.test(channel?.id || "") ||
    channel?.id === INTERNAL_CHANNEL_ID);

export const isAutomatedTeamChannelById = (channelId = "") =>
  STORE_CHANNEL_RE.test(channelId) ||
  TICKET_CHANNEL_RE.test(channelId) ||
  DEPARTMENT_CHANNEL_RE.test(channelId) ||
  SUPPORT_CHANNEL_RE.test(channelId) ||
  /^support-request-[a-f0-9]{32}$/.test(channelId) ||
  channelId === INTERNAL_CHANNEL_ID;

export const isAutomatedChannel = (channel) => {
  if (!channel) return false;
  if (channel.type === "daily" || channel.type === "announcement") return true;
  if (channel.type !== "team") return false;
  return isAutomatedTeamChannelById(channel.id);
};

export const isChannelPinned = (channel) =>
  Boolean(channel?.state?.membership?.pinned_at);

export const classifyChannel = (channel) => {
  if (isChannelPinned(channel)) return SECTION_PINNED;
  if (channel?.type === "messaging") return SECTION_DIRECT;
  if (isTicketChannel(channel)) return SECTION_TICKETS;
  if (isSupportChannel(channel)) return SECTION_SUPPORT;
  if (isAutomatedChannel(channel)) return SECTION_STORE;
  return SECTION_CHANNELS;
};

export const sortChannelsByName = (channels) =>
  [...channels].sort((a, b) =>
    (a?.data?.name || a?.id || "").localeCompare(b?.data?.name || b?.id || "", undefined, {
      sensitivity: "base",
    })
  );

const pinnedAtMs = (channel) => {
  const value = channel?.state?.membership?.pinned_at;
  const ms = value ? new Date(value).getTime() : NaN;
  return Number.isNaN(ms) ? 0 : ms;
};

export const sortPinnedChannels = (channels) =>
  [...channels].sort((a, b) => pinnedAtMs(b) - pinnedAtMs(a));

// Local calendar date as YYYY-MM-DD, matching the backend's daily channel slug
export const getLocalDateStr = (date = new Date()) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

// Daily channel slugs are cut from the store's own calendar day, so label
// them against the store's timezone (falls back to the device date when the
// timezone is unknown or unsupported)
export const getDateStrInTimeZone = (timeZone, date = new Date()) => {
  if (!timeZone) return getLocalDateStr(date);
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).formatToParts(date);
    const get = (type) => parts.find((part) => part.type === type)?.value;
    const year = get("year");
    const month = get("month");
    const day = get("day");
    if (year && month && day) return `${year}-${month}-${day}`;
  } catch (error) {
    // unknown IANA zone or no Intl support
  }
  return getLocalDateStr(date);
};

const addDaysToDateStr = (dateStr, days) => {
  const [year, month, day] = dateStr.split("-").map(Number);
  const date = new Date(year, month - 1, day + days);
  return getLocalDateStr(date);
};

// `today` may be a date string or a resolver (storeId) => date string, so
// daily channels from several stores are each judged in their own timezone
const resolveTodayDateStr = (today, storeId) => {
  if (typeof today === "function") return today(storeId) || getLocalDateStr();
  return today || getLocalDateStr();
};

// Only names from the active company may decorate a daily channel. Never use
// the active/home store's name as a fallback for another location.
export const makeStoreNameMap = (stores = [], companyId) => {
  if (companyId == null || companyId === "") return {};
  return Object.fromEntries(
    (stores || [])
      .filter((store) =>
        store?.id != null && store?.company != null &&
        String(store.company) === String(companyId) &&
        typeof store.store_name === "string" && store.store_name.trim()
      )
      .map((store) => [String(store.id), store.store_name.trim()])
  );
};

export const getDailyChannelLabel = (
  channel,
  today = getLocalDateStr(),
  storeNames = {}
) => {
  if (channel?.type !== "daily") return null;
  const match = DAILY_CHANNEL_RE.exec(channel?.id || "");
  if (!match) return null;
  const [, storeId, channelDateStr] = match;
  const todayDateStr = resolveTodayDateStr(today, storeId);
  let label;
  if (channelDateStr === todayDateStr) label = "Working today";
  else if (channelDateStr === addDaysToDateStr(todayDateStr, 1)) {
    label = "Working tomorrow";
  } else return null;
  const storeName = storeNames?.[storeId];
  return storeName ? `${storeName} · ${label}` : label;
};

// Build a per-store "today" resolver from a {storeId: timezone} map, falling
// back to a default timezone (the active store's) for stores we don't know
export const makeTodayDateResolver = (storeTimezones = {}, fallbackTimezone) =>
  // Computed on every call (cheap) so a screen left open across midnight
  // relabels correctly on its next render
  (storeId) =>
    getDateStrInTimeZone(
      storeTimezones?.[storeId?.toString() || ""] || fallbackTimezone
    );

export const getDirectMessageOtherMembers = (channel, currentUserId) =>
  Object.values(channel?.state?.members || {}).filter(
    ({ user }) => user?.id?.toString() !== currentUserId?.toString()
  );

export const isDirectMessageChannel = (channel) =>
  channel?.type === "messaging";

// A group DM is an unnamed messaging channel with more than two members
export const isGroupDirectMessage = (channel) => {
  if (!isDirectMessageChannel(channel) || channel?.data?.name) return false;
  const memberCount =
    channel?.data?.member_count ??
    Object.keys(channel?.state?.members || {}).length;
  return memberCount > 2;
};

const firstName = (fullName) => (fullName || "").trim().split(" ")[0];

// "Sam, Priya, Jordan, Victor" (all other members; rows/headers truncate
// visually). A maxNames cap collapses the tail to "+N" when a caller needs it.
export const getGroupDirectMessageTitle = (
  channel,
  currentUserId,
  maxNames = Infinity
) => {
  const names = [];
  getDirectMessageOtherMembers(channel, currentUserId).forEach(({ user }) => {
    const name = firstName(user?.name) || user?.id?.toString() || "";
    if (name && !names.includes(name)) names.push(name);
  });
  if (!names.length) return "";
  if (names.length <= maxNames) return names.join(", ");
  return `${names.slice(0, maxNames).join(", ")} +${names.length - maxNames}`;
};

export const getChannelDisplayName = (
  channel,
  currentUserId,
  today = getLocalDateStr(),
  storeNames = {}
) => {
  const dailyLabel = getDailyChannelLabel(channel, today, storeNames);
  if (dailyLabel) return dailyLabel;
  if (channel?.data?.name) return channel.data.name;
  if (isGroupDirectMessage(channel)) {
    return getGroupDirectMessageTitle(channel, currentUserId);
  }
  const otherMember = getDirectMessageOtherMembers(channel, currentUserId)[0];
  return otherMember?.user?.name || "";
};

export const getRoleLabel = (permissionType) => {
  switch (permissionType) {
    case "company_admin":
      return "Admin";
    case "company_manager":
      return "Manager";
    case "rightwork_internal":
      return "Rightwork";
    default:
      return null;
  }
};

export const MAX_DIRECT_MESSAGE_MEMBERS = 8;

// The user's permission at the store a channel belongs to. Channels we create
// carry `store_id`; company-level channels (announcements, support) and
// legacy channels don't, so those fall back to the active store's role.
export const getPermissionRoleForChannel = (
  channel,
  storeMemberships = [],
  fallbackRole = "company_employee"
) => {
  const channelStoreId = channel?.data?.store_id;
  if (channelStoreId === undefined || channelStoreId === null || channelStoreId === "") {
    return fallbackRole;
  }
  const membership = (storeMemberships || []).find(
    (entry) => entry?.store?.toString() === channelStoreId.toString()
  );
  return membership?.permission_type || "company_employee";
};

export const getChannelUnreadCount = (channel) => {
  if (typeof channel?.countUnread === "function") {
    const count = channel.countUnread();
    return Number.isFinite(count) ? count : 0;
  }
  const count = channel?.state?.unreadCount;
  return Number.isFinite(count) ? count : 0;
};

export const SIDEBAR_FILTERS = [
  { key: "all", label: "All" },
  { key: "unread", label: "Unread" },
  { key: "mentions", label: "Mentions" },
];

export const getChannelUnreadMentionCount = (channel) => {
  if (typeof channel?.countUnreadMentions === "function") {
    const count = channel.countUnreadMentions();
    return Number.isFinite(count) ? count : 0;
  }
  return 0;
};

// Sidebar filter chips: "unread" keeps conversations with unread messages,
// "mentions" those where you were @mentioned since you last read
export const filterChannelsForSidebar = (channels = [], filter = "all") => {
  if (filter === "unread") {
    return channels.filter((channel) => getChannelUnreadCount(channel) > 0);
  }
  if (filter === "mentions") {
    return channels.filter((channel) => getChannelUnreadMentionCount(channel) > 0);
  }
  return channels;
};

export const groupChannelsIntoSections = (channels = []) => {
  const buckets = Object.fromEntries(SECTION_ORDER.map((key) => [key, []]));
  channels.forEach((channel) => {
    if (!channel) return;
    buckets[classifyChannel(channel)].push(channel);
  });
  buckets[SECTION_PINNED] = sortPinnedChannels(buckets[SECTION_PINNED]);
  buckets[SECTION_TICKETS] = sortChannelsByName(buckets[SECTION_TICKETS]);
  // Store keeps Stream's recency order like the other sections, so quiet
  // channels (e.g. an unused announcements channel) sink naturally

  return SECTION_ORDER.filter((key) => buckets[key].length > 0).map((key) => ({
    key,
    label: SECTION_LABELS[key],
    channels: buckets[key],
    unreadCount: buckets[key].reduce(
      (total, channel) => total + getChannelUnreadCount(channel),
      0
    ),
  }));
};

export const COLLAPSED_SECTIONS_STORAGE_KEY = "chat-sidebar-collapsed";

export const parseCollapsedSections = (raw) => {
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" && !Array.isArray(parsed)
      ? parsed
      : {};
  } catch (error) {
    return {};
  }
};
